import { CycleReportModal } from './CycleReportModal';
import { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Check, ChevronRight, Save, X, Edit2, Calendar, Target, Activity, RefreshCw, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

const KPI_CATEGORIES = {
  tecnico: [
    { id: 'techServe', name: 'Saque' },
    { id: 'techForehand', name: 'Forehand' },
    { id: 'techBackhand', name: 'Backhand' },
    { id: 'techReturn', name: 'Devolução' },
    { id: 'techNetPlay', name: 'Jogo de Rede/Voleio' }
  ],
  tatico: [
    { id: 'tactRally', name: 'Consistência de Fundo (Rally)' },
    { id: 'tactSelection', name: 'Seleção de Golpes' },
    { id: 'tactPositioning', name: 'Posicionamento em Quadra' },
    { id: 'tactAdaptation', name: 'Adaptação Tática' }
  ],
  fisico: [
    { id: 'physFootwork', name: 'Movimentação (Footwork)' },
    { id: 'physStamina', name: 'Resistência (Stamina)' }
  ],
  mental: [
    { id: 'mentalClutch', name: 'Controle sob Pressão' },
    { id: 'mentalFocus', name: 'Foco/Concentração' }
  ],
  cognitivo: [
    { id: 'cogGameReading', name: 'Leitura de Jogo' },
    { id: 'cogReaction', name: 'Tempo de Reação' },
    { id: 'cogDecision', name: 'Tomada de Decisão' }
  ]
};

export const TrainingPlanManager = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [students, setStudents] = useState<any[]>([]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Form State - Step 1
  const [name, setName] = useState('');
  const [targetType, setTargetType] = useState<'self' | 'student'>('student');
  const [studentId, setStudentId] = useState('');
  const [cycleType, setCycleType] = useState<'Microciclo' | 'Mesociclo' | 'Macrociclo'>('Microciclo');
  const [weeks, setWeeks] = useState<number>(4);
  const [daysPerWeek, setDaysPerWeek] = useState<number>(2);
  const [duration, setDuration] = useState<number>(60);
  const [level, setLevel] = useState<'Iniciante' | 'Intermediário' | 'Avançado'>('Intermediário');

  // Grouping State
  const [buildMethod, setBuildMethod] = useState<'scratch' | 'group'>('scratch');
  const [availableChildPlans, setAvailableChildPlans] = useState<any[]>([]);
  const [selectedChildPlans, setSelectedChildPlans] = useState<string[]>([]);

  // Form State - Step 2
  const [selectedKpis, setSelectedKpis] = useState<{ [key: string]: number }>({});
  
  // Generated Schedule - Step 3
  const [schedule, setSchedule] = useState<any[]>([]);
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);
  const [editingKpi, setEditingKpi] = useState<{ weekIdx: number, dayIdx: number, kpiIdx: number, currentKpiId: string } | null>(null);
  const [customKpiInput, setCustomKpiInput] = useState('');
  
  const [viewMode, setViewMode] = useState<'create' | 'saved'>('create');
  const [savedPlansList, setSavedPlansList] = useState<any[]>([]);

  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);

  const fetchUpdatedList = async () => {
    const token = localStorage.getItem('tt_auth_token');
    try {
      const url = currentUser?.role === 'CLIENTE' 
        ? `${API_URL}/api/training-plans?studentId=${currentUser?.id}`
        : `${API_URL}/api/training-plans?professorId=${currentUser?.id}`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } });
      if(res.ok) {
        const data = await res.json();
        setSavedPlansList(data);
      }
    } catch(e) { console.error(e); }
  };

  useEffect(() => {
    if (viewMode === 'saved') {
      fetchUpdatedList();
    }
  }, [viewMode, currentUser]);

  const handleEditPlan = (plan: any) => {
    setEditingPlanId(plan.id);
    setName(plan.name || '');
    setTargetType(plan.studentId ? 'student' : 'self');
    setStudentId(plan.studentId || '');
    setCycleType(plan.cycleType);
    setWeeks(plan.weeks);
    setDaysPerWeek(plan.daysPerWeek);
    setDuration(plan.durationMins);
    setLevel(plan.athleteLevel);
    
    const kpisDict: {[key: string]: number} = {};
    if (plan.kpis) {
      plan.kpis.forEach((k: any) => { kpisDict[k.kpiName] = k.percentage; });
    }
    setSelectedKpis(kpisDict);
    setSchedule(plan.schedules || []);
    setBuildMethod(plan.childPlans && plan.childPlans.length > 0 ? 'group' : 'scratch');
    setSelectedChildPlans(plan.childPlans ? plan.childPlans.map((c: any) => c.id) : []);
    
    setViewMode('create');
    setStep(1);
  };

  const handleDeletePlan = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este plano? Esta ação não pode ser desfeita.')) return;
    try {
      const res = await fetch(`${API_URL}/api/training-plans/${id}`, { method: 'DELETE', headers: getAuthHeader() });
      if (res.ok) {
        fetchUpdatedList();
        if (savedPlanId === id) setSavedPlanId(null);
      } else alert('Erro ao excluir plano.');
    } catch(e) { console.error(e); }
  };

  const handleArchivePlan = async (id: string) => {
    if (!window.confirm('Deseja arquivar este plano?')) return;
    try {
      const res = await fetch(`${API_URL}/api/training-plans/${id}/archive`, { method: 'PATCH', headers: getAuthHeader() });
      if (res.ok) fetchUpdatedList();
      else alert('Erro ao arquivar plano.');
    } catch(e) { console.error(e); }
  };

  const loadSavedPlan = (plan: any) => {
    setSavedPlanId(plan.id);
    setStudentId(plan.studentId);
    setCycleType(plan.cycleType);
    setSchedule(plan.schedules || []);
    setStep(3);
  };

  const [savedPlanId, setSavedPlanId] = useState<string | null>(null);
  const [rateModal, setRateModal] = useState<{scheduleId: string, exerciseIndex: number, exercise: any} | null>(null);
  const [rateForm, setRateForm] = useState({ score: 0, status: 'EXECUTADO', notes: '', reason: '' });
  const [notesModal, setNotesModal] = useState<{scheduleId: string, coachNotes: string, playerNotes: string} | null>(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  
  const fetchUpdatedPlan = async () => {
    if (!savedPlanId) return;
    try {
      const url = currentUser?.role === 'CLIENTE' 
            ? `${API_URL}/api/training-plans?studentId=${currentUser?.id}`
            : `${API_URL}/api/training-plans?professorId=${currentUser?.id}`;
      const res = await fetch(url, { headers: getAuthHeader() });
      if(res.ok) {
        const data = await res.json();
        const thisPlan = data.find((p: any) => p.id === savedPlanId);
        if(thisPlan) {
          setSchedule(thisPlan.schedules || []);
        }
      }
    } catch(e) { console.error(e); }
  };

  const handleReplaceExercise = async (scheduleId: string, exerciseIndex: number) => {
    if (!savedPlanId) return;
    const reason = window.prompt("Motivo da substituição (opcional):");
    if (reason === null) return;
    try {
      const res = await fetch(`${API_URL}/api/training-plans/${savedPlanId}/schedule/${scheduleId}/replace-exercise/${exerciseIndex}`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify({ studentId: studentId, reason })
      });
      if(res.ok) {
        fetchUpdatedPlan();
      } else {
        alert('Erro ao substituir.');
      }
    } catch(e) { console.error(e); }
  };

  const handleRateExercise = async () => {
    if(!rateModal || !savedPlanId) return;
    try {
      const res = await fetch(`${API_URL}/api/training-plans/${savedPlanId}/schedule/${rateModal.scheduleId}/rate-exercise/${rateModal.exerciseIndex}`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify({ studentId: studentId, score: rateForm.score, status: rateForm.status, notes: rateForm.notes })
      });
      if(res.ok) {
        setRateModal(null);
        fetchUpdatedPlan();
      } else {
        alert('Erro ao avaliar.');
      }
    } catch(e) { console.error(e); }
  };



  const handleSaveNotes = async () => {
    if(!notesModal) return;
    try {
      const sch = schedule.find(s => s.id === notesModal.scheduleId) || {};
      const res = await fetch(`${API_URL}/api/training-plans/schedule/${notesModal.scheduleId}/feedback`, {
        method: 'PUT',
        headers: getAuthHeader(),
        body: JSON.stringify({ feedbackScore: sch.feedbackScore || 0, completed: sch.completed || false, coachNotes: notesModal.coachNotes, playerNotes: notesModal.playerNotes })
      });
      if(res.ok) {
        setNotesModal(null);
        fetchUpdatedPlan();
      } else {
        alert('Erro ao salvar anotações.');
      }
    } catch(e) { console.error(e); }
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('tt_auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const res = await fetch(`${API_URL}/api/class-students`, { headers: getAuthHeader() });
        if (res.ok) {
          const data = await res.json();
          setStudents(data.sort((a: any, b: any) => a.name.localeCompare(b.name)));
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchStudents();
  }, []);

  useEffect(() => {
    if (buildMethod === 'group' && studentId) {
      const fetchAvailable = async () => {
        try {
          const res = await fetch(`${API_URL}/api/training-plans?studentId=${studentId}`, { headers: getAuthHeader() });
          if (res.ok) {
            const data = await res.json();
            const targetTypeForChildren = cycleType === 'Mesociclo' ? 'Microciclo' : (cycleType === 'Macrociclo' ? 'Mesociclo' : '');
            const orphans = data.filter((p: any) => p.cycleType === targetTypeForChildren && !p.parentPlanId);
            setAvailableChildPlans(orphans);
          }
        } catch (e) {
          console.error(e);
        }
      }
      fetchAvailable();
    }
  }, [buildMethod, studentId, cycleType]);

  const totalPercentage = useMemo(() => {
    return Object.values(selectedKpis).reduce((acc, val) => acc + (Number(val) || 0), 0);
  }, [selectedKpis]);

  const handleKpiChange = (kpiId: string, percentage: string) => {
    const num = parseInt(percentage, 10);
    if (isNaN(num) || num < 0) {
      const copy = { ...selectedKpis };
      delete copy[kpiId];
      setSelectedKpis(copy);
    } else {
      setSelectedKpis(prev => ({ ...prev, [kpiId]: num }));
    }
  };

  const generateSchedule = () => {
    const totalDays = weeks * daysPerWeek;
    const kpisPerDay = Math.max(2, Math.floor(duration / 20)); // Cerca de 20 min por exercício
    const totalSlots = totalDays * kpisPerDay;

    // Distribute slots based on percentage
    let slotPool: string[] = [];
    Object.entries(selectedKpis).forEach(([kpiId, percentage]) => {
      const slotsForKpi = Math.round(totalSlots * (percentage / 100));
      for (let i = 0; i < slotsForKpi; i++) {
        slotPool.push(kpiId);
      }
    });

    // Fix rounding issues: if slotPool is less or more than totalSlots
    while (slotPool.length < totalSlots) {
      const keys = Object.keys(selectedKpis);
      slotPool.push(keys[Math.floor(Math.random() * keys.length)]);
    }
    if (slotPool.length > totalSlots) {
      slotPool = slotPool.slice(0, totalSlots);
    }

    // Shuffle pool to distribute randomly
    for (let i = slotPool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [slotPool[i], slotPool[j]] = [slotPool[j], slotPool[i]];
    }

    // Assign to schedule
    const newSchedule = [];
    let poolIndex = 0;
    for (let w = 1; w <= weeks; w++) {
      for (let d = 1; d <= daysPerWeek; d++) {
        const dayKpis = [];
        for (let k = 0; k < kpisPerDay; k++) {
          dayKpis.push(slotPool[poolIndex++]);
        }
        newSchedule.push({
          weekNumber: w,
          dayNumber: d,
          kpis: dayKpis
        });
      }
    }

    setSchedule(newSchedule);
  };

  const handleNextStep = () => {
    if (step === 1) {
      if (!name.trim()) return alert('Por favor, informe o Identificador do Ciclo.');
      if (targetType === 'student' && !studentId) return alert('Selecione um aluno.');
      
      if (buildMethod === 'scratch') {
        if (cycleType === 'Microciclo' && (weeks < 1 || weeks > 2)) return alert('Um Microciclo deve ter entre 1 e 2 semanas.');
        if (cycleType === 'Mesociclo' && (weeks < 3 || weeks > 12)) return alert('Um Mesociclo deve ter entre 3 e 12 semanas.');
        if (cycleType === 'Macrociclo' && (weeks < 24 || weeks > 52)) return alert('Um Macrociclo deve ter entre 24 e 52 semanas.');
        setStep(2);
      } else {
        if (selectedChildPlans.length === 0) return alert('Selecione ao menos um ciclo menor para agrupar.');
        handleSavePlan();
      }
    } else if (step === 2) {
      if (totalPercentage !== 100) return alert(`A soma dos percentuais deve ser exatamente 100%. (Atual: ${totalPercentage}%)`);
      generateSchedule();
      setStep(3);
    }
  };

  const getKpiName = (id: string) => {
    for (const cat of Object.values(KPI_CATEGORIES)) {
      const found = cat.find(k => k.id === id);
      if (found) return found.name;
    }
    return id;
  };

  const handleSavePlan = async () => {
    setLoading(true);
    try {
      const kpisArray = Object.entries(selectedKpis).map(([id, perc]) => {
        let catName = '';
        for (const [key, list] of Object.entries(KPI_CATEGORIES)) {
          if (list.find(k => k.id === id)) catName = key;
        }
        return { kpiCategory: catName, kpiName: id, percentage: perc };
      });

      const payload = {
        name,
        professorId: currentUser?.role === 'CLIENTE' ? currentUser?.id : currentUser?.id,
        studentId: currentUser?.role === 'CLIENTE' ? currentUser?.id : (targetType === 'student' ? studentId : null),
        cycleType,
        weeks: buildMethod === 'group' ? selectedChildPlans.reduce((acc, id) => {
          const p = availableChildPlans.find(c => c.id === id);
          return acc + (p?.weeks || 0);
        }, 0) : weeks,
        daysPerWeek,
        durationMins: duration,
        athleteLevel: level,
        kpis: buildMethod === 'group' ? [] : kpisArray,
        schedules: buildMethod === 'group' ? [] : schedule,
        childPlanIds: buildMethod === 'group' ? selectedChildPlans : undefined
      };

      const url = editingPlanId 
        ? `${API_URL}/api/training-plans/${editingPlanId}` 
        : `${API_URL}/api/training-plans`;
      const method = editingPlanId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getAuthHeader(),
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        setSavedPlanId(data.planId);
        alert(editingPlanId ? 'Plano atualizado com sucesso!' : 'Plano salvo com sucesso!');
        setEditingPlanId(null);
      } else {
        const errorData = await res.json();
        if (res.status === 400 && errorData.error) {
          alert(`Erro: ${errorData.error}`);
        } else {
          alert('Erro ao salvar o plano.');
        }
      }
    } catch (e) {
      console.error(e);
      alert('Erro de conexão ao salvar.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingPlanId(null);
    setName('');
    setStep(1);
    setViewMode('saved');
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '120px 5% 60px', minHeight: '100vh', display: 'flex', flexDirection: 'column', gap: '32px' }}>
      {/* Header */}
      <div className="glass-panel" style={{ padding: '32px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', width: '48px', height: '48px', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <ArrowLeft size={24} />
        </button>
        <div>
          <h1 style={{ fontSize: '32px', color: 'white', fontFamily: 'var(--font-heading)', margin: 0 }}>Plano de Treinos</h1>
          <p style={{ color: 'var(--text-secondary)', marginTop: '8px', marginBottom: 0 }}>Desenvolvimento 360 Graus (Padrão ITF)</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '16px' }}>
        {[1, 2, 3].map(s => (
          <div key={s} style={{ width: '40px', height: '40px', borderRadius: '50%', background: step >= s ? '#3A8E58' : 'rgba(255,255,255,0.1)', color: step >= s ? '#000' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
            {s}
          </div>
        ))}
      </div>

      <div className="glass-panel" style={{ padding: '32px' }}>
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <h2 style={{ color: 'white', fontSize: '24px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Calendar color="#3A8E58" /> 1. {editingPlanId ? 'Editando Ciclo' : 'Configurações do Ciclo'}
              </h2>

              {!editingPlanId && (
                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                  <button 
                    onClick={() => setViewMode('create')} 
                    style={{ flex: 1, padding: '12px', borderRadius: '8px', background: viewMode === 'create' ? '#3A8E58' : 'rgba(255,255,255,0.05)', color: viewMode === 'create' ? '#000' : 'white', fontWeight: 'bold', border: 'none', cursor: 'pointer' }}
                  >
                    Criar Novo Plano
                  </button>
                  <button 
                    onClick={() => setViewMode('saved')} 
                    style={{ flex: 1, padding: '12px', borderRadius: '8px', background: viewMode === 'saved' ? '#3A8E58' : 'rgba(255,255,255,0.05)', color: viewMode === 'saved' ? '#000' : 'white', fontWeight: 'bold', border: 'none', cursor: 'pointer' }}
                  >
                    Ver Planos Salvos
                  </button>
                </div>
              )}

              {viewMode === 'saved' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {savedPlansList.length === 0 ? (
                    <p style={{ color: 'var(--text-secondary)' }}>Nenhum plano salvo encontrado.</p>
                  ) : (
                    savedPlansList.map(plan => (
                      <div key={plan.id} style={{ background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <h3 style={{ color: 'white', margin: '0 0 8px 0' }}>{plan.name}</h3>
                          <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '14px' }}>Ciclo: {plan.cycleType} | Status: {plan.status}</p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={() => loadSavedPlan(plan)} title="Visualizar" style={{ background: '#3A8E58', color: '#000', border: 'none', padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}>
                            Visualizar
                          </button>
                          <button onClick={() => handleEditPlan(plan)} title="Editar" style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer' }}>
                            ✏️
                          </button>
                          {plan.status !== 'ARCHIVED' && (
                            <button onClick={() => handleArchivePlan(plan.id)} title="Arquivar" style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer' }}>
                              📦
                            </button>
                          )}
                          <button onClick={() => handleDeletePlan(plan.id)} title="Excluir" style={{ background: 'rgba(239,68,68,0.2)', color: '#ef4444', border: 'none', padding: '8px', borderRadius: '8px', cursor: 'pointer' }}>
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <>


              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                {/* Target */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Público Alvo</label>
                  <select value={targetType} onChange={(e) => setTargetType(e.target.value as any)} style={{ padding: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <option value="student" style={{color: 'black'}}>Para um Aluno</option>
                    <option value="self" style={{color: 'black'}}>Para Mim Mesmo</option>
                  </select>
                </div>

                {targetType === 'student' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Selecione o Aluno</label>
                    <select value={studentId} onChange={(e) => setStudentId(e.target.value)} style={{ padding: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <option value="" style={{color: 'black'}}>Selecione...</option>
                      {students.map(s => <option key={s.id} value={s.id} style={{color: 'black'}}>{s.name}</option>)}
                    </select>
                  </div>
                )}

                {/* Name */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Identificador do Ciclo (Nome)</label>
                  <input type="text" placeholder="Ex: Microciclo de Base 1" value={name} onChange={(e) => setName(e.target.value)} style={{ padding: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }} />
                </div>

                {/* Cycle */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Tipo de Ciclo (ITF)</label>
                  <select value={cycleType} onChange={(e) => {
                    const v = e.target.value as any;
                    setCycleType(v);
                    if (v === 'Microciclo') setWeeks(1);
                    if (v === 'Mesociclo') setWeeks(3);
                    if (v === 'Macrociclo') setWeeks(24);
                    setBuildMethod('scratch');
                    setSelectedChildPlans([]);
                  }} style={{ padding: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <option value="Microciclo" style={{color: 'black'}}>Microciclo (1 a 2 semanas)</option>
                    <option value="Mesociclo" style={{color: 'black'}}>Mesociclo (3 a 12 semanas)</option>
                    <option value="Macrociclo" style={{color: 'black'}}>Macrociclo (24 a 52 semanas)</option>
                  </select>
                </div>

                {/* Build Method */}
                {cycleType !== 'Microciclo' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', gridColumn: '1 / -1' }}>
                    <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Método de Criação</label>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button onClick={() => { setBuildMethod('scratch'); setSelectedChildPlans([]); }} style={{ flex: 1, padding: '16px', borderRadius: '12px', background: buildMethod === 'scratch' ? 'rgba(58, 142, 88, 0.2)' : 'rgba(255,255,255,0.05)', border: '1px solid', borderColor: buildMethod === 'scratch' ? '#3A8E58' : 'rgba(255,255,255,0.1)', color: buildMethod === 'scratch' ? '#3A8E58' : 'white', cursor: 'pointer' }}>
                        Definir do Zero (Novos KPIs)
                      </button>
                      <button onClick={() => setBuildMethod('group')} style={{ flex: 1, padding: '16px', borderRadius: '12px', background: buildMethod === 'group' ? 'rgba(58, 142, 88, 0.2)' : 'rgba(255,255,255,0.05)', border: '1px solid', borderColor: buildMethod === 'group' ? '#3A8E58' : 'rgba(255,255,255,0.1)', color: buildMethod === 'group' ? '#3A8E58' : 'white', cursor: 'pointer' }}>
                        Agrupar Ciclos Menores
                      </button>
                    </div>
                  </div>
                )}

                {buildMethod === 'scratch' ? (
                  <>
                    {/* Weeks */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Duração em Semanas</label>
                      <input type="number" value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} style={{ padding: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }} />
                    </div>

                {/* Days per Week */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Dias de Treino por Semana</label>
                  <select value={daysPerWeek} onChange={(e) => setDaysPerWeek(Number(e.target.value))} style={{ padding: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>
                    {[1,2,3,4,5,6,7].map(n => <option key={n} value={n} style={{color: 'black'}}>{n}x por semana</option>)}
                  </select>
                </div>

                {/* Duration */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Duração de cada Treino</label>
                  <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} style={{ padding: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <option value={60} style={{color: 'black'}}>60 Minutos</option>
                    <option value={90} style={{color: 'black'}}>90 Minutos</option>
                    <option value={120} style={{color: 'black'}}>120 Minutos</option>
                    <option value={150} style={{color: 'black'}}>150 Minutos</option>
                    <option value={180} style={{color: 'black'}}>180 Minutos</option>
                  </select>
                </div>

                {/* Level */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Nível do Atleta</label>
                  <select value={level} onChange={(e) => setLevel(e.target.value as any)} style={{ padding: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <option value="Iniciante" style={{color: 'black'}}>Iniciante</option>
                    <option value="Intermediário" style={{color: 'black'}}>Intermediário</option>
                    <option value="Avançado" style={{color: 'black'}}>Avançado</option>
                  </select>
                </div>
                  </>
                ) : (
                  <div style={{ gridColumn: '1 / -1', background: 'rgba(0,0,0,0.2)', padding: '24px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <h3 style={{ color: 'white', marginBottom: '16px' }}>Selecione os Ciclos Existentes</h3>
                    {availableChildPlans.length === 0 ? (
                      <p style={{ color: 'var(--text-secondary)' }}>Nenhum ciclo disponível para agrupamento (apenas ciclos órfãos aparecem aqui).</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {availableChildPlans.map(plan => (
                          <label key={plan.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '12px', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)' }}>
                            <input
                              type="checkbox"
                              checked={selectedChildPlans.includes(plan.id)}
                              onChange={(e) => {
                                if (e.target.checked) setSelectedChildPlans(prev => [...prev, plan.id]);
                                else setSelectedChildPlans(prev => prev.filter(id => id !== plan.id));
                              }}
                              style={{ width: '20px', height: '20px' }}
                            />
                            <div style={{ color: 'white', display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontWeight: 600 }}>{plan.name || 'Ciclo Sem Nome'}</span>
                              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{plan.cycleType} • {plan.weeks} semana(s)</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px' }}>
                <button onClick={handleNextStep} disabled={loading} style={{ background: '#3A8E58', color: '#000', padding: '16px 32px', borderRadius: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', border: 'none' }}>
                  {buildMethod === 'group' ? (loading ? 'Salvando...' : <><Save size={20} /> Salvar Plano</>) : <>Avançar <ChevronRight size={20} /></>}
                </button>
              </div>
                </>
              )}
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ color: 'white', fontSize: '24px', display: 'flex', alignItems: 'center', gap: '12px', margin: 0 }}>
                  <Target color="#3A8E58" /> 2. Alocação de KPIs
                </h2>
                <div style={{ background: totalPercentage === 100 ? 'rgba(58, 142, 88, 0.2)' : 'rgba(239, 68, 68, 0.2)', color: totalPercentage === 100 ? '#3A8E58' : '#ef4444', padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Activity size={18} /> Total: {totalPercentage}% / 100%
                </div>
              </div>

              <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
                Defina a porcentagem de foco para cada aspecto neste ciclo. A soma de todos os KPIs deve ser exatamente 100%. (Em branco = 0%)
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {Object.entries(KPI_CATEGORIES).map(([catKey, kpis]) => (
                  <div key={catKey} style={{ background: 'rgba(255,255,255,0.02)', padding: '24px', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <h3 style={{ color: 'white', textTransform: 'capitalize', fontSize: '18px', marginBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '8px' }}>{catKey}</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
                      {kpis.map(kpi => (
                        <div key={kpi.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '8px' }}>
                          <span style={{ color: 'var(--text-secondary)', fontSize: '14px', flex: 1 }}>{kpi.name}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={selectedKpis[kpi.id] || ''}
                              onChange={(e) => handleKpiChange(kpi.id, e.target.value)}
                              placeholder="0"
                              style={{ width: '60px', padding: '8px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}
                            />
                            <span style={{ color: 'white', fontWeight: 'bold' }}>%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px' }}>
                <button onClick={() => setStep(1)} style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '16px 32px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', border: 'none' }}>
                  Voltar
                </button>
                <button onClick={handleNextStep} disabled={totalPercentage !== 100} style={{ background: totalPercentage === 100 ? '#3A8E58' : 'rgba(255,255,255,0.1)', color: totalPercentage === 100 ? '#000' : 'gray', padding: '16px 32px', borderRadius: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: totalPercentage === 100 ? 'pointer' : 'not-allowed', border: 'none' }}>
                  Gerar Tabela <ChevronRight size={20} />
                </button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ color: 'white', fontSize: '24px', display: 'flex', alignItems: 'center', gap: '12px', margin: 0 }}>
                  <Calendar color="#3A8E58" /> 3. Cronograma Gerado
                </h2>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={() => setIsEditingSchedule(!isEditingSchedule)} style={{ background: isEditingSchedule ? 'rgba(58, 142, 88, 0.2)' : 'rgba(255,255,255,0.1)', color: isEditingSchedule ? '#3A8E58' : 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                    {isEditingSchedule ? <><Check size={16} /> Concluído</> : <><Edit2 size={16} /> Editar</>}
                  </button>
                </div>
              </div>

              <div style={{ overflowX: 'auto', background: 'rgba(0,0,0,0.2)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', color: 'white' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)', textAlign: 'left', width: '120px' }}>Semana</th>
                      {Array.from({ length: daysPerWeek }).map((_, i) => (
                        <th key={i} style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)', textAlign: 'left' }}>Dia {i + 1}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: weeks }).map((_, wIdx) => {
                      const weekSchedules = schedule.filter(s => s.weekNumber === wIdx + 1);
                      return (
                        <tr key={wIdx}>
                          <td style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.05)', fontWeight: 'bold', color: 'var(--text-secondary)' }}>
                            Semana {wIdx + 1}
                          </td>
                          {Array.from({ length: daysPerWeek }).map((_, dIdx) => {
                            const daySch = weekSchedules.find(s => s.dayNumber === dIdx + 1);
                            return (
                              <td key={dIdx} style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                {daySch ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    
                  {daySch.exercises && daySch.exercises.length > 0 ? (
                      daySch.exercises.map((ex: any, idx: number) => (
                        <div key={idx} style={{ background: ex.status === 'EXECUTADO' ? 'rgba(58, 142, 88, 0.2)' : ex.status === 'DESCARTADO' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255,255,255,0.05)', color: 'white', padding: '12px', borderRadius: '8px', fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>{ex.title}</span>
                                <span>{ex.score ? `⭐ ${ex.score}` : ''}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button onClick={() => window.open((ex.pdfUrl || '').replace('techtennis.vercel.app', 'www.techtennisbr.com'), '_blank')} style={{ background: '#3A8E58', color: '#000', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', flex: 1, fontWeight: 'bold' }}>PDF</button>
                                <button onClick={() => handleReplaceExercise(daySch.id, idx)} style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }} title="Substituir Exercício"><RefreshCw size={14} /></button>
                                <button onClick={() => { setRateModal({ scheduleId: daySch.id, exerciseIndex: idx, exercise: ex }); setRateForm({ score: ex.score || 0, status: ex.status || 'EXECUTADO', notes: '', reason: '' }); }} style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }} title="Avaliar Exercício"><Star size={14} /></button>
                            </div>
                        </div>
                      ))
                  ) : (
                      daySch.kpis.map((kpiId: string, idx: number) => (

                                      <div key={idx} style={{ background: 'rgba(58, 142, 88, 0.1)', color: '#3A8E58', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        {getKpiName(kpiId)}
                                        {isEditingSchedule && (
                                          <button 
                                            onClick={() => {
                                              setEditingKpi({ weekIdx: wIdx + 1, dayIdx: dIdx + 1, kpiIdx: idx, currentKpiId: kpiId });
                                              setCustomKpiInput('');
                                            }}
                                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0 }}
                                          >
                                            <X size={14} />
                                          </button>
                                        )}
                                      </div>
                                    )))}
                                  </div>
                                ) : '-'}
                                {daySch && (
                                  <div style={{ marginTop: '12px' }}>
                                    <button onClick={() => setNotesModal({ scheduleId: daySch.id, coachNotes: daySch.coachNotes || '', playerNotes: daySch.playerNotes || '' })} style={{ width: '100%', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', padding: '6px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>
                                      Anotações do Dia
                                    </button>
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '32px' }}>
                <button onClick={() => setStep(2)} disabled={loading} style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '16px 32px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', border: 'none' }}>
                  Reconfigurar KPIs
                </button>
                <div style={{ display: 'flex', gap: '16px' }}>
                  {editingPlanId ? (
                    <button onClick={handleCancelEdit} disabled={loading} style={{ background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', padding: '16px 32px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                      Cancelar Edição
                    </button>
                  ) : (
                    <button onClick={() => navigate('/')} disabled={loading} style={{ background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', padding: '16px 32px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                      Rejeitar / Cancelar
                    </button>
                  )}
                  <button onClick={handleSavePlan} disabled={loading || isEditingSchedule || savedPlanId !== null} style={{ background: savedPlanId ? 'rgba(255,255,255,0.1)' : '#3A8E58', color: savedPlanId ? 'gray' : '#000', padding: '16px 32px', borderRadius: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: (loading || isEditingSchedule || savedPlanId) ? 'not-allowed' : 'pointer', border: 'none', opacity: (loading || isEditingSchedule) ? 0.5 : 1 }}>
                    {loading ? 'Salvando...' : savedPlanId ? 'Plano Salvo ✔' : editingPlanId ? 'Atualizar Plano' : 'Aprovar e Salvar Plano'} <Save size={20} />
                  </button>
                </div>
                
                {savedPlanId && !editingPlanId && (
                  <div style={{ marginTop: '32px', padding: '24px', background: 'rgba(58, 142, 88, 0.1)', border: '1px solid #3A8E58', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <h3 style={{ margin: 0, color: 'white' }}>4. Plano Salvo com Sucesso! (ID: {savedPlanId})</h3>
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>O que você deseja fazer agora?</p>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <button 
                        onClick={async () => {
                          try {
                            const res = await fetch(`${API_URL}/api/training-plans/${savedPlanId}/generate-exercises`, {
                              method: 'POST',
                              headers: getAuthHeader()
                            });
                            if (res.ok) {
                              alert('Exercícios gerados com sucesso e anexados ao seu cronograma!');
                              fetchUpdatedPlan();
                            } else {
                              alert('Erro ao gerar exercícios. Verifique se os PDFs estão cadastrados.');
                            }
                          } catch (err) {
                            alert('Erro de conexão ao gerar exercícios.');
                          }
                        }} 
                        style={{ background: '#3A8E58', color: '#000', border: 'none', padding: '16px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', flex: 1 }}
                      >
                        Gerar Exercícios de Quadra
                      </button>
                      <button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', padding: '16px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', flex: 1 }}>
                        Voltar ao Início
                      </button>
                    </div>
                    <div style={{ display: 'flex', marginTop: '8px' }}>
                      <button onClick={() => setReportModalOpen(true)} style={{ background: '#3A8E58', color: '#000', border: 'none', padding: '16px', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer', flex: 1 }}>
                        Gerar PDF do Cronograma
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {editingKpi && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div style={{ background: '#1c1c1e', padding: '24px', borderRadius: '16px', width: '100%', maxWidth: '400px', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ color: 'white', margin: 0 }}>Substituir KPI</h3>
              <button onClick={() => setEditingKpi(null)} style={{ background: 'none', border: 'none', color: 'gray', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            
            <div style={{ maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ color: 'gray', fontSize: '12px', fontWeight: 'bold' }}>SELECIONAR EXISTENTE</span>
              {Object.entries(KPI_CATEGORIES).map(([catKey, kpis]) => (
                <div key={catKey} style={{ marginBottom: '8px' }}>
                  <div style={{ color: '#3A8E58', fontSize: '11px', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '4px' }}>{catKey}</div>
                  {kpis.map(kpi => (
                    <button 
                      key={kpi.id}
                      onClick={() => {
                        const updated = [...schedule];
                        const target = updated.find(u => u.weekNumber === editingKpi.weekIdx && u.dayNumber === editingKpi.dayIdx);
                        if (target) {
                          target.kpis[editingKpi.kpiIdx] = kpi.id;
                          setSchedule(updated);
                        }
                        setEditingKpi(null);
                      }}
                      style={{ width: '100%', textAlign: 'left', padding: '10px', background: 'rgba(255,255,255,0.05)', border: 'none', color: 'white', borderRadius: '8px', marginBottom: '4px', cursor: 'pointer' }}
                    >
                      {kpi.name}
                    </button>
                  ))}
                </div>
              ))}
            </div>

            <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)', margin: '8px 0' }} />
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ color: 'gray', fontSize: '12px', fontWeight: 'bold' }}>OU DIGITE UM NOVO</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input 
                  type="text" 
                  value={customKpiInput} 
                  onChange={(e) => setCustomKpiInput(e.target.value)} 
                  placeholder="Nome ou ID do KPI" 
                  style={{ flex: 1, padding: '12px', borderRadius: '8px', background: 'rgba(0,0,0,0.5)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }} 
                />
                <button 
                  onClick={() => {
                    if (!customKpiInput.trim()) return;
                    const updated = [...schedule];
                    const target = updated.find(u => u.weekNumber === editingKpi.weekIdx && u.dayNumber === editingKpi.dayIdx);
                    if (target) {
                      target.kpis[editingKpi.kpiIdx] = customKpiInput.trim();
                      setSchedule(updated);
                    }
                    setEditingKpi(null);
                  }}
                  style={{ background: '#3A8E58', color: '#000', padding: '0 16px', borderRadius: '8px', fontWeight: 'bold', border: 'none', cursor: 'pointer' }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {rateModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#1A102F', padding: '24px', borderRadius: '12px', width: '400px', maxWidth: '90%', color: 'white' }}>
            <h3 style={{ marginTop: 0, color: '#3A8E58' }}>Avaliar Exercício</h3>
            <p style={{ fontSize: '14px', marginBottom: '16px' }}>{rateModal.exercise?.title}</p>
            
            <label style={{ display: 'block', marginBottom: '8px' }}>Status:</label>
            <select value={rateForm.status} onChange={e => setRateForm({...rateForm, status: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '16px', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid #3A8E58', borderRadius: '4px' }}>
              <option value="EXECUTADO" style={{ color: 'black' }}>Executado</option>
              <option value="DESCARTADO" style={{ color: 'black' }}>Descartado</option>
            </select>

            <label style={{ display: 'block', marginBottom: '8px' }}>Nota (1 a 5):</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              {[1,2,3,4,5].map(n => (
                <button key={n} onClick={() => setRateForm({...rateForm, score: n})} style={{ padding: '8px 16px', background: rateForm.score === n ? '#3A8E58' : 'rgba(255,255,255,0.1)', color: rateForm.score === n ? 'black' : 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>{n}</button>
              ))}
            </div>

            <label style={{ display: 'block', marginBottom: '8px' }}>Observações (opcional):</label>
            <textarea value={rateForm.notes} onChange={e => setRateForm({...rateForm, notes: e.target.value})} rows={3} style={{ width: '100%', padding: '8px', marginBottom: '24px', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid #3A8E58', borderRadius: '4px' }}></textarea>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button onClick={() => setRateModal(null)} style={{ background: 'transparent', color: 'white', border: 'none', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={handleRateExercise} style={{ background: '#3A8E58', color: 'black', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {notesModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#1A102F', padding: '24px', borderRadius: '12px', width: '500px', maxWidth: '90%', color: 'white' }}>
            <h3 style={{ marginTop: 0, color: '#3A8E58' }}>Anotações do Treino</h3>
            
            <label style={{ display: 'block', marginBottom: '8px', color: 'gray' }}>Anotações do Treinador:</label>
            <textarea 
              value={notesModal.coachNotes} 
              onChange={e => setNotesModal({...notesModal, coachNotes: e.target.value})} 
              rows={4} 
              disabled={currentUser?.role === 'CLIENTE'}
              style={{ width: '100%', padding: '12px', marginBottom: '16px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', opacity: currentUser?.role === 'CLIENTE' ? 0.7 : 1 }}
              placeholder="Exclusivo do treinador..."
            />

            <label style={{ display: 'block', marginBottom: '8px', color: 'gray' }}>Anotações do Aluno:</label>
            <textarea 
              value={notesModal.playerNotes} 
              onChange={e => setNotesModal({...notesModal, playerNotes: e.target.value})} 
              rows={4} 
              style={{ width: '100%', padding: '12px', marginBottom: '24px', background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
              placeholder="Feedback do aluno sobre o treino..."
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button onClick={() => setNotesModal(null)} style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={handleSaveNotes} style={{ background: '#3A8E58', color: 'black', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Salvar Anotações</button>
            </div>
          </div>
        </div>
      )}

      {reportModalOpen && (
        <CycleReportModal 
          isOpen={reportModalOpen} 
          onClose={() => setReportModalOpen(false)} 
          schedule={schedule} 
          cycleType={cycleType}
          studentName={students.find(s => s.id === studentId)?.name || 'Aluno'}
        />
      )}
    </div>
  );
};
